// @ts-strict-ignore
import type {HoveredItem} from 'common/charts/contexts/segment-filter-controller';
import {LegendOverride, LegendOverrides} from 'common/types/reports/bookmark';
import {MIXPANEL_DEFAULT_THEME_ID} from 'common/query-builder/chart-customization/colors/constants';
import {CurrentProjectResource} from 'common/report/model-resources/projects';
import {
  createTheme,
  deleteTheme,
  Theme,
  themesClient,
  ThemesRequestType,
  ThemesResultsMapType,
  updateTheme,
} from 'common/report/queries/app-api/themes';
import {ApiResource} from 'common/util/model-resource/api-resource';

const CREATE_KEY = `create`;

export class ThemesResource extends ApiResource<Theme> {
  declare currentProject: CurrentProjectResource;
  declare globalHoveredItem: HoveredItem;
  declare _legendOverrides: LegendOverrides;
  apiClient = themesClient;

  /** Holds a map of segments to colors for rendering charts.
   * This is used in the context of a board where comparing like-to-like
   * segments is important for consuming and quickly scan data.
   */
  declare colorMappings: Map<string, {color: string; sourceId: string}>;

  constructor({currentProject}: {currentProject: CurrentProjectResource}) {
    super();
    this.currentProject = currentProject;
    this.globalHoveredItem = null;
    this._legendOverrides = {};
    this.colorMappings = new Map();
  }

  private async requestWrapper(loadingKey: string, callback: () => Promise<ThemesResultsMapType>) {
    this.fetchesInProgress.set(loadingKey, undefined);
    this.notifySubscribers();
    try {
      const results = await callback();

      this.update(results, {partialUpdate: false});

      return results;
    } finally {
      this.fetchesInProgress.delete(loadingKey);
      this.notifySubscribers();
    }
  }

  /** when a user retrieves a list of themes, we only want to present those they have access to */
  getAllValues() {
    return super.getAllValues().filter((theme) => theme.can_view);
  }

  patch(id: number, theme: Partial<Theme>): Promise<ThemesResultsMapType> {
    const themeId = id.toString();
    return this.requestWrapper(themeId, () => updateTheme({themeId, theme}));
  }

  create(theme: ThemesRequestType): Promise<ThemesResultsMapType> {
    return this.requestWrapper(CREATE_KEY, () => createTheme(theme));
  }

  async delete(id: number): Promise<ThemesResultsMapType> {
    const themeId = id.toString();
    const results = await deleteTheme({themeId});
    this.models.delete(themeId);
    this.apiClient.purgeCache();
    this.notifySubscribers();
    return results;
  }

  isLoading(id?: number) {
    if (id) {
      return this.fetchesInProgress.has(id.toString());
    }
    return this.fetchesInProgress.has(CREATE_KEY);
  }

  getDefaultProjectThemeId() {
    const themeId = this.currentProject.get().value?.defaultThemeId;
    if (!themeId) {
      return MIXPANEL_DEFAULT_THEME_ID;
    }

    return themeId;
  }

  isDarkMode() {
    return window.document.documentElement.classList.contains(`dark-mode`);
  }

  setDarkMode(on = false) {
    if (on) {
      window.document.documentElement.classList.add(`dark-mode`);
    } else {
      window.document.documentElement.classList.remove(`dark-mode`);
    }
    this.notifySubscribers();
  }

  setGlobalHoveredItem(item: HoveredItem | null) {
    this.globalHoveredItem = item;
    this.notifySubscribers();
  }

  set legendOverrides(overrides: LegendOverrides) {
    this._legendOverrides = overrides;
    this.notifySubscribers();
  }

  get legendOverrides() {
    return this._legendOverrides;
  }

  setLegendOverride(key: string, value: LegendOverride | null) {
    if (value === null) {
      delete this._legendOverrides[key];
    } else {
      this._legendOverrides[key] = value;
    }
    this.notifySubscribers();
  }

  setColorMapping(path: string, color: string, sourceId: string) {
    this.colorMappings.set(path, {color, sourceId});
  }

  clearSourceColorMappings(sourceId: string) {
    const keys = Array.from(this.colorMappings.keys());
    for (const key of keys) {
      if (this.colorMappings.get(key).sourceId === sourceId) {
        this.colorMappings.delete(key);
      }
    }
  }

  resetColorMappings() {
    this.colorMappings = new Map();
  }
}
